/**
 * Baby Development Phase Edit Page
 * Edit subtitle and description for a phase
 */

import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { ArrowLeft, Save, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';
import type { BabyDevelopmentPhase } from './types';

interface FormData {
  subtitle: string;
  description: string;
}

export function BabyDevelopmentPhaseEditPage() {
  const supabase = getSupabaseAdminClient();
  const { id: phaseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();

  const { register, handleSubmit, reset, control, formState: { errors, isDirty } } = useForm<FormData>();

  const { data: phase, isLoading } = useQuery({
    queryKey: ['baby-development-phase', phaseId, contentLocale],
    queryFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const { data, error } = await (supabase as any)
        .from('baby_development_phases')
        .select('*')
        .eq('id', phaseId)
        .eq('locale', contentLocale)
        .single();

      if (error) throw error;
      return data as BabyDevelopmentPhase;
    },
    enabled: !!phaseId,
  });

  useEffect(() => {
    if (phase) {
      reset({
        subtitle: phase.subtitle,
        description: phase.description,
      });
    }
  }, [phase, reset]);

  const updateMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!phaseId) throw new Error('No phase ID');
      const { error } = await (supabase as any)
        .from('baby_development_phases')
        .update({
          subtitle: data.subtitle,
          description: data.description,
          locale: contentLocale,
        })
        .eq('id', phaseId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Phase aktualisiert');
      queryClient.invalidateQueries({ queryKey: ['baby-development-phases'] });
      queryClient.invalidateQueries({ queryKey: ['baby-development-phase', phaseId, contentLocale] });
    },
    onError: (error) => {
      toast.error('Fehler beim Speichern: ' + (error as Error).message);
    },
  });

  const onSubmit = (data: FormData) => {
    updateMutation.mutate(data);
  };

  // Early return after all hooks
  if (!phaseId) {
    return <div className="text-center py-12 text-slate-500">Phase nicht gefunden</div>;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!phase) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Phase nicht gefunden</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/features/baby-development')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex-1">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-teal-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900">Phase {phase.phase_number}: {phase.display_range}</h1>
              <p className="text-slate-500">Bearbeite Subtitle und Beschreibung dieser Phase</p>
            </div>
            <LocaleSelector />
          </div>
        </div>
      </div>

      {/* Fixed info */}
      <div className="bg-slate-50 rounded-lg p-4">
        <h3 className="font-medium text-slate-900 mb-2">Feste Werte (nicht editierbar)</h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Zeitraum:</span>
            <span className="ml-2 font-medium">{phase.display_range}</span>
          </div>
          <div>
            <span className="text-slate-500">Wochen:</span>
            <span className="ml-2 font-medium">{phase.min_age_weeks} - {phase.max_age_weeks}</span>
          </div>
          <div>
            <span className="text-slate-500">Phase:</span>
            <span className="ml-2 font-medium">{phase.phase_number} von 5</span>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Subtitle
          </label>
          <input
            {...register('subtitle', { required: 'Subtitle ist erforderlich' })}
            type="text"
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="z.B. Entdecken & Greifen"
          />
          {errors.subtitle && (
            <p className="mt-1 text-sm text-red-600">{errors.subtitle.message}</p>
          )}
          <p className="mt-1 text-xs text-slate-500">
            Kurzer, prägnanter Titel für diese Phase (max. 100 Zeichen)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Beschreibung
          </label>
          <Controller
            name="description"
            control={control}
            rules={{ required: 'Beschreibung ist erforderlich' }}
            render={({ field }) => (
              <RichTextEditor
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Beschreibe, was in dieser Phase passiert..."
                minHeight="150px"
              />
            )}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
          <p className="mt-1 text-xs text-slate-500">
            2-4 Sätze, die beschreiben was Eltern in dieser Phase erwarten können
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/features/baby-development')}
            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={!isDirty || updateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {updateMutation.isPending ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </form>
    </div>
  );
}
